import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { API_URL } from '../config/api';
import Sidebar from './Sidebar';
import MapView from './MapView';
import StatusAntena from './StatusAntena';
import CadastroEquipamento from './CadastroEquipamento';
import ListarLocais from './ListarLocais';
import ListarAcessos from './ListarAcessos';
import RelatorioHotspot from './RelatorioHotspot';
import Graficos from './Graficos';
import ConfigRedesSociais from './ConfigRedesSociais';
import './Dashboard.css';

const Dashboard = () => {
  const { user, logout } = useAuth();
  const [menuOpen, setMenuOpen] = useState(true);
  const [currentView, setCurrentView] = useState('mapa');
  const [equipamentos, setEquipamentos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [redesSociaisOpen, setRedesSociaisOpen] = useState(false);
  const [sseConnected, setSseConnected] = useState(false);

  const fetchEquipamentos = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        window.location.href = '/sysconect/adm-login';
        return;
      }
      const response = await axios.get(`${API_URL}/api/equipamentos`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEquipamentos(response.data || []);
      setLoading(false);
    } catch (error) {
      if (error.response?.status === 401 || error.response?.status === 403) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        window.location.href = '/sysconect/adm-login';
        return;
      }
      setEquipamentos([]);
      setLoading(false);
    }
  }, []);

  // Realtime (SSE): ao receber push do MK, atualiza imediatamente a lista (e o mapa muda na hora).
  useEffect(() => {
    if (!['mapa', 'status'].includes(currentView)) return undefined;
    const token = localStorage.getItem('token');
    if (!token) return undefined;

    const url = `${API_URL}/api/stream/equipamentos?token=${encodeURIComponent(token)}`;
    const es = new EventSource(url);

    setSseConnected(false);
    let pending = false;
    const triggerRefresh = () => {
      if (pending) return;
      pending = true;
      // Agrupa bursts de eventos (cidade toda enviando) em uma única atualização.
      setTimeout(async () => {
        pending = false;
        await fetchEquipamentos();
      }, 250);
    };

    /** Atualiza o equipamento na lista na hora (mapa fica verde/vermelho sem esperar o refetch). */
    const applySseEquipamentoPatch = (data) => {
      if (!data?.id) return;
      setEquipamentos((prev) => {
        const idx = prev.findIndex((e) => Number(e.id) === Number(data.id));
        if (idx === -1) return prev;
        const next = [...prev];
        const eq = { ...next[idx] };
        if (data.mk_ultima_identity != null && String(data.mk_ultima_identity).trim() !== '') {
          eq.mk_ultima_identity = data.mk_ultima_identity;
        }
        if (data.velocidade_upload != null && String(data.velocidade_upload).trim() !== '') {
          eq.velocidade_upload = data.velocidade_upload;
        }
        if (data.velocidade_download != null && String(data.velocidade_download).trim() !== '') {
          eq.velocidade_download = data.velocidade_download;
        }
        if (data.mk_ultimo_sinal_em) {
          eq.mk_ultimo_sinal_em = data.mk_ultimo_sinal_em;
        }
        if (data.status_internet !== null && data.status_internet !== undefined) {
          eq.status_internet = data.status_internet;
        }
        if (typeof data.status_antena_online === 'boolean') {
          eq.status_antena_online = data.status_antena_online;
        }
        next[idx] = eq;
        return next;
      });
    };

    es.addEventListener('ready', () => {
      setSseConnected(true);
      // força 1 refresh inicial ao conectar
      triggerRefresh();
    });
    es.addEventListener('equipamento_status', (ev) => {
      try {
        const data = JSON.parse(ev.data);
        applySseEquipamentoPatch(data);
      } catch (_err) {
        // ignora JSON inválido
      }
      triggerRefresh();
    });
    es.onerror = () => {
      // fallback: o polling já cobre; SSE tenta reconectar automaticamente.
      setSseConnected(false);
    };

    return () => {
      setSseConnected(false);
      es.close();
    };
  }, [currentView, fetchEquipamentos]);

  // Na tela Status: atualizar com frequência para refletir upload/download vindos da API (MK/cron).
  // Nas demais telas: intervalo maior para reduzir carga.
  useEffect(() => {
    fetchEquipamentos();
    // Mapa precisa refletir rapidamente os pushes do MikroTik.
    const realtimeView = currentView === 'mapa' || currentView === 'status';
    const pollMs = realtimeView
      ? (sseConnected ? 120000 : (currentView === 'status' ? 12000 : 5000))
      : 300000;
    const interval = setInterval(fetchEquipamentos, pollMs);
    return () => clearInterval(interval);
  }, [currentView, fetchEquipamentos, sseConnected]);

  const handleLogout = () => {
    logout();
    window.location.href = '/sysconect/adm-login';
  };

  const handleNavigate = (view) => {
    const token = localStorage.getItem('token');
    if (!token || !user) {
      handleLogout();
      return;
    }
    setCurrentView(view);
  };

  console.log('Dashboard: Renderizando, user:', user ? 'autenticado' : 'não autenticado');

  if (!user) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        fontSize: '18px',
        color: '#dc3545'
      }}>
        Usuário não autenticado. Redirecionando...
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      <Sidebar
        menuOpen={menuOpen}
        setMenuOpen={setMenuOpen}
        currentView={currentView}
        onNavigate={handleNavigate}
        onLogout={handleLogout}
        user={user}
        onOpenRedesSociais={() => setRedesSociaisOpen(true)}
      />
      <ConfigRedesSociais
        isOpen={redesSociaisOpen}
        onClose={() => setRedesSociaisOpen(false)}
      />
      <div className={`dashboard-content ${menuOpen ? 'sidebar-open' : 'sidebar-closed'}`}>
        {currentView === 'mapa' && (
          <MapView
            equipamentos={equipamentos}
            loading={loading}
            onNovoCadastro={() => setCurrentView('cadastro')}
            layoutKey={menuOpen ? 'sidebar-open' : 'sidebar-closed'}
          />
        )}
        {currentView === 'status' && (
          <StatusAntena
            equipamentos={equipamentos}
            loading={loading}
            onEquipamentoUpdated={fetchEquipamentos}
          />
        )}
        {currentView === 'cadastro' && (
          <CadastroEquipamento onEquipamentoAdded={fetchEquipamentos} />
        )}
        {currentView === 'listar' && (
          <ListarLocais
            equipamentos={equipamentos}
            loading={loading}
            onEquipamentoUpdated={fetchEquipamentos}
          />
        )}
        {currentView === 'acessos' && (
          <ListarAcessos />
        )}
        {currentView === 'relatorio-hotspot' && (
          <RelatorioHotspot />
        )}
        {currentView === 'graficos' && (
          <Graficos />
        )}
        {!['mapa', 'status', 'cadastro', 'listar', 'acessos', 'relatorio-hotspot', 'graficos'].includes(currentView) && (
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100vh',
            fontSize: '18px',
            color: '#666'
          }}>
            Visualização não encontrada: {currentView}
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
